/**
 * DH Commerce Rule Engine
 * Framework baseline: v1.4
 *
 * Evaluates storefront conditions and triggers actions without
 * hardcoding every promotion directly into theme sections.
 */

(function (window, document) {
  "use strict";

  var DHCommerce = window.DHCommerce = window.DHCommerce || {};

  DHCommerce.state = DHCommerce.state || {};
  DHCommerce.rules = DHCommerce.rules || [];
  DHCommerce.actions = DHCommerce.actions || {};
  DHCommerce.conditions = DHCommerce.conditions || {};

  function getByPath(object, path) {
    if (!object || !path) {
      return undefined;
    }

    return String(path)
      .split(".")
      .reduce(function (value, key) {
        return value == null ? undefined : value[key];
      }, object);
  }

  function asArray(value) {
    return Array.isArray(value) ? value : [value];
  }

  function compare(actual, operator, expected) {
    switch (operator) {
      case "eq":
        return actual === expected;

      case "neq":
        return actual !== expected;

      case "gt":
        return Number(actual) > Number(expected);

      case "gte":
        return Number(actual) >= Number(expected);

      case "lt":
        return Number(actual) < Number(expected);

      case "lte":
        return Number(actual) <= Number(expected);

      case "includes":
        if (Array.isArray(actual)) {
          return actual.indexOf(expected) !== -1;
        }

        return String(actual || "")
          .toLowerCase()
          .indexOf(String(expected || "").toLowerCase()) !== -1;

      case "in":
        return asArray(expected).indexOf(actual) !== -1;

      case "exists":
        return expected ? actual != null : actual == null;

      default:
        console.warn(
          "DH Commerce: Unknown rule operator:",
          operator
        );

        return false;
    }
  }

  function evaluateCondition(condition, context) {
    if (!condition || typeof condition !== "object") {
      return false;
    }

    if (Array.isArray(condition.all)) {
      return condition.all.every(function (item) {
        return evaluateCondition(item, context);
      });
    }

    if (Array.isArray(condition.any)) {
      return condition.any.some(function (item) {
        return evaluateCondition(item, context);
      });
    }

    if (condition.not) {
      return !evaluateCondition(
        condition.not,
        context
      );
    }

    if (
      condition.type &&
      typeof DHCommerce.conditions[condition.type] === "function"
    ) {
      try {
        return Boolean(
          DHCommerce.conditions[condition.type](
            condition,
            context
          )
        );
      } catch (error) {
        console.warn(
          "DH Commerce condition failed:",
          condition.type,
          error
        );

        return false;
      }
    }

    var actual = getByPath(
      context,
      condition.field
    );

    return compare(
      actual,
      condition.operator || "eq",
      condition.value
    );
  }

  function executeAction(action, context, rule) {
    if (!action || !action.type) {
      return Promise.resolve();
    }

    var handler = DHCommerce.actions[action.type];

    if (typeof handler !== "function") {
      console.warn(
        "DH Commerce: No action handler registered for",
        action.type
      );

      return Promise.resolve();
    }

    try {
      return Promise.resolve(
        handler(
          action,
          context,
          rule
        )
      );
    } catch (error) {
      console.warn(
        "DH Commerce action failed:",
        action.type,
        error
      );

      return Promise.resolve();
    }
  }

  DHCommerce.ruleEngine = {
    register: function (rule) {
      if (!rule || !rule.id) {
        console.warn(
          "DH Commerce: Rule requires an id."
        );

        return;
      }

      var existingIndex = DHCommerce.rules.findIndex(
        function (item) {
          return item.id === rule.id;
        }
      );

      if (existingIndex >= 0) {
        DHCommerce.rules[existingIndex] = rule;
      } else {
        DHCommerce.rules.push(rule);
      }
    },

    remove: function (ruleId) {
      DHCommerce.rules = DHCommerce.rules.filter(
        function (rule) {
          return rule.id !== ruleId;
        }
      );
    },

    evaluate: function (context) {
      context = context || DHCommerce.state || {};

      var matchingRules = DHCommerce.rules
        .filter(function (rule) {
          if (rule.enabled === false) {
            return false;
          }

          if (!rule.when) {
            return true;
          }

          return evaluateCondition(
            rule.when,
            context
          );
        })
        .sort(function (a, b) {
          return Number(b.priority || 0) -
            Number(a.priority || 0);
        });

      var sequence = Promise.resolve();

      matchingRules.forEach(function (rule) {
        asArray(rule.then || []).forEach(
          function (action) {
            sequence = sequence.then(
              function () {
                return executeAction(
                  action,
                  context,
                  rule
                );
              }
            );
          }
        );
      });

      return sequence.then(function () {
        return matchingRules;
      });
    },

    test: function (rule, context) {
      if (!rule || !rule.when) {
        return true;
      }

      return evaluateCondition(
        rule.when,
        context || DHCommerce.state || {}
      );
    }
  };

  /**
   * Built-in action:
   * Dispatch a custom browser event.
   */
  DHCommerce.actions.dispatchEvent = function (
    action,
    context,
    rule
  ) {
    document.dispatchEvent(
      new CustomEvent(
        action.eventName || "dhcommerce:rule",
        {
          detail: {
            rule: rule,
            action: action,
            context: context
          }
        }
      )
    );
  };

  /**
   * Built-in action:
   * Show or hide an element.
   */
  DHCommerce.actions.toggleElement = function (
    action
  ) {
    var element = document.querySelector(
      action.selector
    );

    if (!element) {
      return;
    }

    var shouldShow = action.show !== false;

    element.hidden = !shouldShow;

    element.setAttribute(
      "aria-hidden",
      shouldShow ? "false" : "true"
    );
  };

  /**
   * Built-in action:
   * Change text inside an element.
   */
  DHCommerce.actions.setText = function (
    action
  ) {
    var element = document.querySelector(
      action.selector
    );

    if (!element) {
      return;
    }

    element.textContent = action.text || "";
  };

  /**
   * Built-in action:
   * Add a CSS class.
   */
  DHCommerce.actions.addClass = function (
    action
  ) {
    var element = document.querySelector(
      action.selector
    );

    if (!element || !action.className) {
      return;
    }

    element.classList.add(
      action.className
    );
  };

  /**
   * Built-in action:
   * Remove a CSS class.
   */
  DHCommerce.actions.removeClass = function (
    action
  ) {
    var element = document.querySelector(
      action.selector
    );

    if (!element || !action.className) {
      return;
    }

    element.classList.remove(
      action.className
    );
  };

  /**
   * Built-in condition:
   * Match the current weekday.
   *
   * Sunday = 0
   * Monday = 1
   * Tuesday = 2
   * Wednesday = 3
   * Thursday = 4
   * Friday = 5
   * Saturday = 6
   */
  DHCommerce.conditions.weekday = function (
    condition
  ) {
    return asArray(condition.value).indexOf(
      new Date().getDay()
    ) !== -1;
  };

  /**
   * Built-in condition:
   * Match a URL query parameter.
   */
  DHCommerce.conditions.queryParameter = function (
    condition
  ) {
    var params = new URLSearchParams(
      window.location.search
    );

    var actual = params.get(
      condition.name
    );

    return compare(
      actual,
      condition.operator || "eq",
      condition.value
    );
  };

  /**
   * Re-evaluate rules whenever the framework
   * announces a state change.
   */
  document.addEventListener(
    "dhcommerce:state-changed",
    function (event) {
      var state =
        event.detail &&
        event.detail.state
          ? event.detail.state
          : DHCommerce.state;

      DHCommerce.ruleEngine.evaluate(
        state
      );
    }
  );

  document.dispatchEvent(
    new CustomEvent(
      "dhcommerce:rule-engine-ready",
      {
        detail: {
          version: "1.4"
        }
      }
    )
  );

})(window, document);